import { useCallback, useState } from 'react';
import { Button } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { useAuth } from '../../../auth/AuthContext';

interface DownloadPdfLinkProps {
  label: string;
  pdfKey: string | null;
  /** API path (no leading `/api/v1`, matches `src/api/client.ts`'s convention — e.g.
   * `/invoicing/invoices/${id}/pdf`) for the authenticated download route
   * (`services/api/src/documents/download.ts`). */
  downloadPath: string;
}

/** Mirrors `src/features/reports/csv.ts`'s `useCsvDownload`'s established pattern:
 * a plain `<a href>` can't carry the `Authorization`/`X-Tenant-Id` headers the API
 * requires (services/api's middleware/auth.ts contract), so this fetches the PDF as
 * a Blob (with the same bearer/tenant headers every other request carries) and
 * triggers a normal browser file-save via a temporary object-URL anchor.
 *
 * This works unchanged regardless of which `DocumentStore` backs the API
 * (services/api/src/documents/get-document-store.ts): the local-dev store streams
 * bytes directly; the S3-backed store 302-redirects to a short-lived presigned URL,
 * which `fetch` follows transparently — this component never needs to know which one
 * is active. */
const DEFAULT_BASE_URL = 'http://localhost:3001/api/v1';

function baseUrl(): string {
  return import.meta.env.VITE_API_URL ?? DEFAULT_BASE_URL;
}

function buildDownloadUrl(path: string): string {
  return `${baseUrl().replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
}

export function DownloadPdfLink({ label, pdfKey, downloadPath }: DownloadPdfLinkProps) {
  const auth = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    setError(null);
    try {
      const headers: Record<string, string> = {};
      if (auth.token) headers.Authorization = `Bearer ${auth.token}`;
      if (auth.activeTenant) headers['X-Tenant-Id'] = auth.activeTenant.tenantId;

      const response = await fetch(buildDownloadUrl(downloadPath), { headers });
      if (!response.ok) {
        throw new Error(`Document download failed (HTTP ${response.status})`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${label}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Document download failed');
    } finally {
      setDownloading(false);
    }
  }, [auth.token, auth.activeTenant, downloadPath, label]);

  if (!pdfKey) return null;
  return (
    <Button
      onClick={handleDownload}
      loading={downloading}
      variant="light"
      color={error ? 'red' : undefined}
      leftSection={<IconDownload size={16} />}
    >
      {error ? `${label} — retry` : label}
    </Button>
  );
}
